// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';
import { Cell, Row } from 'react-table';

interface CustomGroupedRowProps {
    /**
     * Index to apply as the `aria-rowindex` attribute - see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Row_Role}
     */
    ariaRowIndex: number;
    /**
     * Whether the `matched_work_id` has been changed by the user in the dropdown in the
     * StatementWorkCell. When true, the row will be grayed out as a hint to the user that they need
     * to apply the edits to see updated values.
     */
    matchedWorkIdWasEdited: boolean;
    /**
     * Row height
     */
    height: number;
    /**
     * Tailwind size to apply to text for grouped rows
     */
    groupTextSize: 'xs' | 'sm' | 'base' | 'lg';
    /**
     * The react-table row instance
     */
    row: Row<TrqkGapChannelAudio>;
}

/**
 * Renders the content for a grouped table row.
 * @param props - {@link CustomGroupedRowProps}
 */
export function CustomGroupedRow({
    ariaRowIndex,
    height,
    matchedWorkIdWasEdited,
    groupTextSize,
    row,
}: CustomGroupedRowProps): JSX.Element {
    const { key: rowKey, ...rowRest } = row.getRowProps();

    return (
        <div
            key={rowKey}
            {...rowRest}
            aria-rowindex={ariaRowIndex}
            className={cx('tr relative flex items-center border-tertiary-200', {
                'border-t': ariaRowIndex > 1,
                'bg-white border-b': row.isExpanded,
                'text-tertiary-300 italic': matchedWorkIdWasEdited,
            })}
            style={{
                height,
            }}
        >
            {row.cells.map((cell: Cell<TrqkGapChannelAudio>) => {
                const { key: cellKey, ...cellRest } = cell.getCellProps();
                const { isAggregated, isGrouped, isPlaceholder } = cell;
                const showGrouped = isGrouped;
                const showAggregated = !isGrouped && isAggregated;
                const showCell = !isGrouped && !isAggregated && !isPlaceholder;

                return (
                    <div
                        key={cellKey}
                        {...cellRest}
                        className={`td text-${groupTextSize}`}
                    >
                        {showGrouped && <>{cell.render('Grouped')}</>}
                        {showAggregated && <>{cell.render('Aggregated')}</>}
                        {showCell && <>{cell.render('Cell')}</>}
                    </div>
                );
            })}
        </div>
    );
}
